// Keep this as it's still being used by base/claim-card/claims-leave-plan.tsx
// in the next few sprint it should be updated!

import React from 'react';
import styled from 'styled-components';
import { colors, fonts } from '../../style';
import * as dm from '../../data-models';
import dayjs from 'dayjs';
import { Icon, IconButton } from '../absence-history/index.sc';
import BluePen from '../../images/blue-pen.svg';
import { Popup } from '@progress/kendo-react-popup';
import { Link } from 'react-router-dom';
import { enums } from '../../utils';
import { getLeavePlanData, getCalendarDetails } from '../../utils/calendars';
import Button from '../base/button';
import Calendar from '../generic-calendar';
import DetailModal from './detail-modal';
import { intakeStyle, continuousStyle } from './calendar-styles';
import LoadingIndicatorCard from './loading-indicator-card';
import { PersonDataContext } from '../../contexts/person-data-context';
import isBetween from 'dayjs/plugin/isBetween';
dayjs.extend(isBetween);

const dtFormat = 'MMM DD, YYYY';

interface LeavePlanProps {
  systemId?: string;
  keyId?: string;
  visualType: 'intake' | 'continuous';
  moveToStep?: Function;
  editConfig?: any;
  leaves?: dm.Leave[];
  claims?: dm.Claim[];
  calendarData?: dm.Calendar[];
}

const Container = styled.div`
  background-color: ${colors.white};
  border-radius: 8px;
  box-shadow: 0px 3px 16px #00000029;
  padding: 32px;
  width: 100%;

  @media (max-width: 1279px) {
    padding: 25px 16px 16px;
  }
`;

const TitleBar = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const Header = styled.h1`
  color: ${colors.prussianblue};
  font-family: ${fonts.ubuntu.regular.fontFamily};
  font-size: 28px;
  font-weight: ${fonts.ubuntu.regular.fontWeight};
  line-height: 32px;
  margin: 0;
  white-space: nowrap;
  margin-right: auto;

  @media (max-width: 1180px) {
    font-size: 24px;
    margin-bottom: 24px;
  }

  @media (max-width: 330px) {
    margin-bottom: 15px;
  }
`;

const Text = styled.p`
  color: ${colors.warmNeutralL3};
  font-family: ${fonts.openSans.regular.fontFamily};
  font-size: 14px;
  font-weight: ${fonts.openSans.regular.fontWeight};
  line-height: 22px;
`;

const IconButtonSingle = styled(IconButton)`
  border-radius: 4px;
  max-width: 82px;
  margin-left: 16px;
  order: 3;

  @media (max-width: 1180px) {
    order: 2;
  }

  @media (max-width: 330px) {
    margin-left: 0;
    margin-bottom: 15px;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: start;
  flex-direction: row;
  flex-wrap: nowrap;

  @media (max-width: 1180px) {
    flex-wrap: wrap;
  }
`;

const PopupContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 6px 16px;

  a {
    color: ${colors.denim};
  }
`;

const CalendarContainer = styled.div`
  position: relative;
`;

const getPeriodInfo = (periods: any) => {
  if (periods.length > 1) {
    const earliest = dayjs.min(periods.map((p: any) => dayjs(p.startDate)));
    const latest = dayjs.max(periods.map((p: any) => dayjs(p.endDate)));
    return (
      <Text>
        You've requested multiple leave periods starting{' '}
        <strong>
          {dayjs(earliest).format('MMM DD')} until{' '}
          {dayjs(latest).format(dtFormat)}.
        </strong>
      </Text>
    );
  }

  return periods.length > 0 ? (
    <Text>
      You've requested leave for{' '}
      <strong>
        {dayjs(periods[0].endDate).diff(dayjs(periods[0].startDate), 'day')}{' '}
        days,
      </strong>{' '}
      from{' '}
      <strong>
        {dayjs(periods[0].startDate).format('MMM DD')} until{' '}
        {dayjs(periods[0].endDate).format(dtFormat)}.
      </strong>
    </Text>
  ) : null;
};

interface IHandleLeavePlanInit {
  props: LeavePlanProps;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setDays: React.Dispatch<React.SetStateAction<any>>;
  setElements: React.Dispatch<React.SetStateAction<any>>;
  setPeriods: React.Dispatch<React.SetStateAction<any>>;
  setBenefits: React.Dispatch<React.SetStateAction<any>>;
  setInitialCalendarDate: React.Dispatch<React.SetStateAction<string>>;
  leaves?: dm.Leave[];
}

const getOverallLeavePeriodForNTN = (leaves?: dm.Leave[]) => {
  let startDate = undefined;
  let endDate = undefined;
  if (leaves && leaves.length > 0) {
    startDate = dayjs
      .min(leaves.map((l) => dayjs(l.startDate)))
      ?.toISOString?.();
    endDate = dayjs.max(leaves.map((l) => dayjs(l.endDate)))?.toISOString?.();
  }
  return { startDate, endDate };
};

const handleLeavePlanInit = async ({
  props,
  setLoading,
  setDays,
  setElements,
  setPeriods,
  setBenefits,
  setInitialCalendarDate,
  leaves,
}: IHandleLeavePlanInit) => {
  const { startDate, endDate } = getOverallLeavePeriodForNTN(props?.leaves);
  const calendarData = props.calendarData
    ? props.calendarData
    : await getCalendarDetails(
        startDate,
        endDate,
        leaves,
        props.systemId,
        props.visualType === 'intake',
      );

  if (!dayjs().isBetween(dayjs(startDate), dayjs(endDate), 'd', '[]')) {
    setInitialCalendarDate(dayjs(startDate).format('YYYY-MM-DD'));
  }

  const result = getLeavePlanData(
    calendarData,
    props.visualType,
    props.systemId,
  );

  setElements(result.elements);
  setPeriods(result.nonOverlappingPeriods);
  setBenefits(result.benefits);
  setDays(result.dayDetails);

  setLoading(false);
};

const LeavePlan: (p: LeavePlanProps) => React.ReactElement = (p) => {
  const [displayEdit, setDisplayEdit] = React.useState(false);
  const [days, setDays] = React.useState<any>([]);
  const [elements, setElements] = React.useState<any>([]);
  const [periods, setPeriods] = React.useState<any>([]);
  const [benefits, setBenefits] = React.useState<any>([]);
  const [loading, setLoading] = React.useState(true);
  const [initialCalendarDate, setInitialCalendarDate] = React.useState(
    dayjs().format('YYYY-MM-DD'),
  );
  const anchor = React.useRef<HTMLButtonElement | null>(null);
  const { personData } = React.useContext(PersonDataContext);

  const showEditPopup = (e: any) => {
    e.preventDefault();
    setDisplayEdit(!displayEdit);
  };

  const edit = (index: number, elementName: string) => {
    p?.moveToStep?.({ value: index }, elementName);
  };

  React.useEffect(() => {
    let ignore = false;
    handleLeavePlanInit({
      props: p,
      setLoading: (v) => {
        if (!ignore) setLoading(v);
      },
      setDays: (v) => {
        if (!ignore) setDays(v);
      },
      setElements: (v) => {
        if (!ignore) setElements(v);
      },
      setPeriods: (v) => {
        if (!ignore) setPeriods(v);
      },
      setBenefits: (v) => {
        if (!ignore) setBenefits(v);
      },
      setInitialCalendarDate: (v) => {
        if (!ignore) setInitialCalendarDate(v);
      },
      leaves: personData?.cases?.leaves,
    });
    return () => {
      ignore = true;
    };
  }, [p, personData]);

  const editConfig = p?.editConfig ?? {};

  const legend = p.visualType === 'intake' ? intakeStyle : continuousStyle;

  if (loading) {
    return <LoadingIndicatorCard />;
  }

  if (
    p.visualType === 'intake' &&
    benefits?.jobProtectionBenefits?.length === 0 &&
    benefits?.incomeProtectionBenefits?.length === 0
  ) {
    return (
      <div className="info-bubble">
        <p>
          <strong>Your leave plan was built successfully!</strong>
        </p>
        <p>Please click "Next" to proceed.</p>
      </div>
    );
  }

  return (
    <Container id="leave-plan-container">
      <TitleBar>
        <HeaderContainer>
          <Header>
            {p.visualType === 'intake'
              ? 'Leave Estimate'
              : 'Your Leave Calendar'}
          </Header>
          {editConfig?.allowEdit === 'true' && (
            <IconButtonSingle
              id="lpv-edit-button"
              onClick={showEditPopup}
              ref={anchor}
            >
              <Icon src={BluePen} alt="" height="16px" width="16px" /> Edit
            </IconButtonSingle>
          )}
        </HeaderContainer>
      </TitleBar>
      {getPeriodInfo(periods)}
      <CalendarContainer>
        <Calendar
          {...p}
          legend={legend}
          dates={days}
          defaultSelectedDate={initialCalendarDate}
          customDetailModal={(args: any) =>
            DetailModal({
              ...args,
              legend,
            })
          }
          periods={periods}
          elements={elements}
          showLegend={true}
          hideUnusedStyles={true}
        />
      </CalendarContainer>
      <EditPopover
        anchor={anchor}
        editConfig={editConfig}
        edit={edit}
        displayEdit={displayEdit}
        visualType={p.visualType}
      />
    </Container>
  );
};

interface IEditPopover {
  anchor: React.MutableRefObject<HTMLButtonElement | null>;
  editConfig: any;
  edit: (index: number, elementName: string) => void;
  displayEdit: boolean;
  visualType: string;
}

const EditPopover: (p: IEditPopover) => React.ReactElement = (p) => {
  const { anchor, editConfig, edit, displayEdit, visualType } = p;
  return (
    <Popup
      id="lpv-edit-popover"
      anchor={anchor.current}
      anchorAlign={{
        horizontal: 'right',
        vertical: 'bottom',
      }}
      popupAlign={{
        horizontal: 'right',
        vertical: 'top',
      }}
      show={displayEdit}
      popupClass={'popup-content'}
      margin={{ vertical: 5, horizontal: 0 }}
      style={{
        boxShadow: '0px 3px 6px #00000029',
        borderRadius: '8px',
      }}
    >
      {visualType === 'intake' ? (
        <PopupContent id="lpv-intake-popover-content">
          {editConfig?.editLinks?.map(
            (
              option: {
                linkText: string;
                formStepIndex: number;
                formFieldName: string;
              },
              i: number,
            ) => (
              <Button
                key={`edit-option-${option.formFieldName}-${i}`}
                id={`lpv-edit-option-${i}`}
                buttonStyle={enums.ButtonTypes.link}
                style={{ textAlign: 'left' }}
                onClick={() => edit(option.formStepIndex, option.formFieldName)}
              >
                {option.linkText}
              </Button>
            ),
          )}
        </PopupContent>
      ) : (
        <PopupContent id="lpv-ocm-popover-content">
          <Link to={`${enums.AppPaths.form}/last-day-worked`}>
            Revise your last day worked
          </Link>
          <Link to={`${enums.AppPaths.form}/return-to-work`}>
            Set a new return to work date
          </Link>
          <Link to={`${enums.AppPaths.form}/delivery-details`}>
            Move your delivery date
          </Link>
        </PopupContent>
      )}
    </Popup>
  );
};

export default LeavePlan;
